import { Upload, X } from "lucide-react";
import { Label } from "../ui/label";
import { useCallback, useState } from "react";

export const ListingImageEdit = ({ 
  imagePreview, 
  onImageChange, 
  onRemoveImage, 
  error, 
  isEdit = false,
  currentImageUrl = null 
}) => {
  const [isDragActive, setIsDragActive] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      onImageChange(acceptedFiles[0]);
    }
  }, [onImageChange]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files?.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/') && file.size <= 5 * 1024 * 1024) {
        onDrop([file]);
      }
    }
  }, [onDrop]);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      onDrop([file]);
    }
  };

  const handleClick = () => {
    document.getElementById('file-input').click();
  };

  const hasImage = imagePreview || currentImageUrl;
  const isNewImage = imagePreview && imagePreview !== currentImageUrl;

  return (
    <div className="space-y-2">
      <Label>
        Image {!isEdit && <span className="text-red-500">*</span>}
      </Label>

      {hasImage ? (
        <div className="relative rounded-lg overflow-hidden border border-border">
          <img 
            src={imagePreview || currentImageUrl} 
            alt="Preview" 
            className="w-full aspect-video object-cover"
          />
          
          {/* Show indicator for new image */}
          {isNewImage && (
            <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-xs">
              New image
            </div>
          )}
          
          <button
            type="button"
            onClick={onRemoveImage}
            className="absolute top-2 right-2 p-1 bg-black/50 rounded-full text-white hover:bg-black/70 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <div 
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={handleClick}
          className={`border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer transition-colors ${
            isDragActive 
              ? 'border-igbanwo-mint bg-igbanwo-mint/5' 
              : error 
                ? 'border-red-300 bg-red-50' 
                : 'border-border hover:border-igbanwo-mint/60'
          }`}
        >
          <input 
            id="file-input"
            type="file"
            accept="image/*"
            onChange={handleFileInput}
            className="hidden"
          />
          <Upload className={`h-8 w-8 mb-2 ${error ? 'text-red-400' : 'text-igbanwo-mint'}`} />
          <p className="text-sm mb-1 text-muted-foreground">
            {isDragActive ? 'Drop your image here!' : 'Drag and drop an image or click to browse'}
          </p>
          <p className="text-xs text-muted-foreground">
            PNG, JPG up to 5MB
          </p>
        </div>
      )}
      
      {/* Change image button when editing */}
      {isEdit && hasImage && (
        <button
          type="button"
          onClick={handleClick}
          className="text-sm text-igbanwo-mint hover:text-igbanwo-mint/80 underline"
        >
          Change image
        </button>
      )}
      
      {error && <p className="text-sm text-red-600">{error}</p>}
      
      <input 
        id="file-input"
        type="file"
        accept="image/*"
        onChange={handleFileInput}
        className="hidden"
      />
    </div>
  );
};